import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Download, FileText, Check, Sparkles } from "lucide-react";

const PDF_URL = "/downloads/hydration-ritual-guide.pdf";

interface PdfLeadMagnetProps {
  source?: string;
  className?: string;
}

export const PdfLeadMagnet = ({
  source = "pdf-ritual-guide",
  className = "",
}: PdfLeadMagnetProps) => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDone, setIsDone] = useState(false);

  const validateEmail = (value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value) && value.length <= 255;
  };

  const triggerDownload = () => {
    const link = document.createElement("a");
    link.href = PDF_URL;
    link.download = "hydration-ritual-guide.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();

    if (!trimmed || !validateEmail(trimmed)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("newsletter_subscribers")
        .insert({ email: trimmed, source });

      if (error && error.code !== "23505") throw error;

      try {
        const { error: fnError } = await supabase.functions.invoke("send-welcome-email", {
          body: { email: trimmed },
        });
        if (fnError) console.error("Welcome email error:", fnError);
      } catch (emailError) {
        console.error("Welcome email error:", emailError);
      }

      setIsDone(true);
      triggerDownload();
      toast.success("Guide is downloading — we also sent it to your inbox.");
    } catch (err) {
      console.error("PDF lead magnet error:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section
      id="free-guide"
      aria-labelledby="free-guide-heading"
      className={`bg-card border border-border rounded-2xl p-6 sm:p-10 shadow-sm ${className}`}
    >
      <div className="grid md:grid-cols-[1fr_1.2fr] gap-8 items-center">
        <div className="order-2 md:order-1">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-primary font-medium mb-3">
            <Sparkles className="w-4 h-4" aria-hidden="true" />
            Free download
          </div>
          <h2
            id="free-guide-heading"
            className="text-2xl sm:text-3xl font-semibold text-foreground mb-3 leading-tight"
          >
            The 7-day crystal hydration ritual
          </h2>
          <p className="text-muted-foreground mb-5 leading-relaxed">
            A gentle PDF guide with daily practices, journal prompts, and a
            safety checklist. Slow down, drink with intention, and build a
            hydration habit that actually sticks.
          </p>
          <ul className="space-y-2 mb-6 text-sm text-foreground">
            <li className="flex gap-2">
              <Check className="w-4 h-4 text-primary mt-1 shrink-0" aria-hidden="true" />
              7 daily rituals (5 minutes each)
            </li>
            <li className="flex gap-2">
              <Check className="w-4 h-4 text-primary mt-1 shrink-0" aria-hidden="true" />
              Journal prompts + intention framework
            </li>
            <li className="flex gap-2">
              <Check className="w-4 h-4 text-primary mt-1 shrink-0" aria-hidden="true" />
              Crystal safety checklist
            </li>
          </ul>

          {isDone ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-3 bg-primary/10 rounded-lg">
                <Check className="w-5 h-5 text-primary" aria-hidden="true" />
                <span className="text-foreground font-medium">
                  Guide sent — enjoy your ritual.
                </span>
              </div>
              <a
                href={PDF_URL}
                download
                className="inline-flex items-center gap-2 text-sm text-primary hover:underline font-medium"
              >
                <Download className="w-4 h-4" aria-hidden="true" />
                Download again
              </a>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <label htmlFor="pdf-lead-email" className="sr-only">
                Email address
              </label>
              <Input
                id="pdf-lead-email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                maxLength={255}
                required
                aria-required="true"
              />
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full sm:w-auto"
                aria-label="Get the free hydration ritual PDF guide"
              >
                <Download className="w-4 h-4 mr-2" aria-hidden="true" />
                {isLoading ? "Sending..." : "Get the free guide"}
              </Button>
            </form>
          )}

          <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
            By downloading you agree to receive occasional wellness emails.
            Unsubscribe anytime. This guide is for lifestyle inspiration only —
            not medical advice. <strong>Affiliate disclaimer:</strong> this site
            participates in affiliate programs and may earn a commission from
            qualifying purchases at no extra cost to you.
          </p>
        </div>

        <div className="order-1 md:order-2 flex justify-center">
          <div
            aria-hidden="true"
            className="relative w-full max-w-[280px] aspect-[3/4] bg-gradient-to-br from-primary/15 via-accent/10 to-secondary/20 rounded-xl border border-border shadow-md flex flex-col items-center justify-center p-6 text-center"
          >
            <FileText className="w-14 h-14 text-primary mb-4" />
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
              PDF Guide
            </div>
            <div className="font-semibold text-foreground text-lg leading-snug">
              7-Day Crystal Hydration Ritual
            </div>
            <div className="mt-4 text-xs text-muted-foreground">
              Free · Instant download
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PdfLeadMagnet;
